// src/SwimMapPage.js
import React, { useState, useMemo, useRef, useEffect } from 'react';
import Map, { Marker, Popup, Source, Layer } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useMapboxToken } from './MapboxTokenContext';


const lineLayerStyle = {
  id: 'route-line',
  type: 'line',
  source: 'route',
  layout: { 'line-join': 'round', 'line-cap': 'round' },
  paint: { 'line-color': '#007bff', 'line-width': 4 },
};

const SwimMapPage = ({ swims, selectedSwim, activeRoute, onSwimSelect, routeLoading }) => {
  const mapboxToken = useMapboxToken();
  const mapRef = useRef();
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/streets-v11');

  // --- THIS IS THE NEW LOGIC ---
  // Calculate the initial map state before rendering.
  const getInitialState = () => {
    if (selectedSwim && selectedSwim.geoBoundingBox) {
      // If a swim is selected on load, center on it.
      const { minLat, maxLat, minLng, maxLng } = selectedSwim.geoBoundingBox;
      const swimBounds = new mapboxgl.LngLatBounds(
          [minLng, minLat],
          [maxLng, maxLat]
        );
      return {
        bounds: swimBounds,
        fitBoundsOptions: {
          padding: 20
        }
      };
    }
    // Otherwise, use the default world view.
    return {
      longitude: -98.5795,
      latitude: 39.8283,
      zoom: 1,
    };
  };
  // --- END OF NEW LOGIC ---

  // Effect to fly to a *newly* selected swim's location AFTER the initial load.
  useEffect(() => {
    if (selectedSwim && selectedSwim.geoBoundingBox && mapRef.current) {
      const ZOOM_THRESHOLD = 10; // Don't re-frame if zoomed in further than this
      const currentZoom = mapRef.current.getZoom();
      const { minLat, maxLat, minLng, maxLng } = selectedSwim.geoBoundingBox;

      if (currentZoom > ZOOM_THRESHOLD) {
        const swimBounds = new mapboxgl.LngLatBounds(
          [minLng, minLat],
          [maxLng, maxLat]
        );
        const mapBounds = mapRef.current.getBounds();

        // If the swim is already visible on screen, don't move the map.
        if (mapBounds.contains(swimBounds.getNorthEast()) && mapBounds.contains(swimBounds.getSouthWest())) {
          return;
        }
      }

      const bounds = [[minLng, minLat], [maxLng, maxLat]];
      // If zoomed out, fit the bounds of the swim.
      mapRef.current.fitBounds(bounds, {
        padding: 20,
        duration: 1500
      });
    }
  }, [selectedSwim]);

  const pins = useMemo(
    () =>
      swims.map((swim) => {
        let pinLat, pinLng;

        // Prioritize the precise start coordinate
        if (swim.startCoordinate) {
          pinLat = swim.startCoordinate._latitude;
          pinLng = swim.startCoordinate._longitude;
        } else if (swim.geoBoundingBox) {
          // Fallback to the center of the bounding box
          const { minLat, maxLat, minLng, maxLng } = swim.geoBoundingBox;
          pinLat = minLat + (maxLat - minLat) / 2;
          pinLng = minLng + (maxLng - minLng) / 2;
        } else {
          // If we have no coordinates, we can't render a pin for this swim
          return null;
        }

        return (
          <Marker
            key={`marker-${swim.id}`}
            longitude={pinLng}
            latitude={pinLat}
            anchor="bottom"
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              onSwimSelect(swim.id);
            }}
          >
            <div style={{ color: '#007bff', fontSize: '24px', cursor: 'pointer' }}>📍</div>
          </Marker>
        );
      }),
    [swims, onSwimSelect]
  );

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Map
        ref={mapRef}
        // Use the calculated initial state here.
        initialViewState={getInitialState()}
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyle}
        mapboxAccessToken={mapboxToken}
      >
        {pins}

        {activeRoute && (
          <Source id="route" type="geojson" data={activeRoute}>
            <Layer {...lineLayerStyle} />
          </Source>
        )}

        {selectedSwim && (selectedSwim.startCoordinate || selectedSwim.geoBoundingBox) && (
          <Popup
            anchor="top"
            longitude={
              selectedSwim.startCoordinate?._longitude ??
              (selectedSwim.geoBoundingBox.minLng + (selectedSwim.geoBoundingBox.maxLng - selectedSwim.geoBoundingBox.minLng) / 2)
            }
            latitude={
              selectedSwim.startCoordinate?._latitude ??
              (selectedSwim.geoBoundingBox.minLat + (selectedSwim.geoBoundingBox.maxLat - selectedSwim.geoBoundingBox.minLat) / 2)
            }
            onClose={() => onSwimSelect(null)}
            closeOnClick={false}
          >
        <div className="popup-user-info">
          <img src={selectedSwim.userProfile?.profilePictureUrl || '/placeholder-user.svg'} width="40" className="popup-user-avatar" alt={selectedSwim.userProfile?.displayName || 'User'} />
              <strong>{selectedSwim.userProfile?.displayName}</strong>
            </div>
            {routeLoading && <p>Loading route...</p>}
          </Popup>
        )}
      </Map>
      <div className="map-style-switcher">
        <button onClick={() => setMapStyle('mapbox://styles/mapbox/streets-v11')} className={mapStyle === 'mapbox://styles/mapbox/streets-v11' ? 'active' : ''}>
          Streets
        </button>
        <button onClick={() => setMapStyle('mapbox://styles/mapbox/satellite-streets-v12')} className={mapStyle === 'mapbox://styles/mapbox/satellite-streets-v12' ? 'active' : ''}
        >
          Satellite
        </button>
      </div>
    </div>
  );
};

export default SwimMapPage;
